import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  messageAPI, tenantAPI, Tenant,
  SentMessageRow, MessageDetail, MessageOverviewSummary,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Icon from '../components/Icon';
import StatTile from '../components/ui/StatTile';
import ChannelBubble from '../components/ui/ChannelBubble';
import Empty from '../components/ui/Empty';
import Pagination from '../components/Pagination';
import ViberMessagePreview from '../components/ViberMessagePreview';
import { fmt, fmtDate } from '../utils/format';
import type { Channel } from '../utils/channel';

/** Avatar initials for a recipient. */
const initials = (n?: string | null, fb?: string | null) => {
  const base = n || fb || '';
  const parts = base.trim().split(/\s+/);
  return (parts.slice(0, 2).map((p) => p[0] || '').join('') || base[0] || '?').toUpperCase();
};

/** Full date+time for the detail panel. */
const fmtDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const ClientOverview: React.FC = () => {
  const t = useT();
  const { role } = useAuth();
  const isSuperAdmin = role === 'super_admin';

  // Optional per-contact scope (opened from the contact popup). When set, the
  // page shows only that contact's messages.
  const [searchParams, setSearchParams] = useSearchParams();
  const contactId = parseInt(searchParams.get('contactId') || '', 10) || 0;

  /** Status → badge appearance, reusing the existing badge/ch-tag classes. */
  const statusBadge = (status: string): React.ReactNode => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered' || s === 'seen')
      return <span className="ch-tag email" style={{ fontSize: 11.5 }}><Icon name="check" size={12} /> {t('Isporučeno', 'Delivered')}</span>;
    if (s === 'failed' || s === 'bounced')
      return <span className="badge badge-failed"><span className="dot" />{t('Neuspelo', 'Failed')}</span>;
    if (s === 'sent' || s === 'queued')
      return <span className="badge badge-sending"><span className="dot" />{t('Poslato', 'Sent')}</span>;
    return <span className="badge badge-draft"><span className="dot" />{t('Na čekanju', 'Pending')}</span>;
  };

  // ---- filters / paging ----
  const [status, setStatus] = useState('all');
  const [channel, setChannel] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // ---- super-admin tenant scope ----
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<number | ''>('');

  // ---- data ----
  const [messages, setMessages] = useState<SentMessageRow[]>([]);
  const [summary, setSummary] = useState<MessageOverviewSummary | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');

  // ---- detail modal ----
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Debounce the free-text search into `query`.
  useEffect(() => {
    const h = setTimeout(() => { setQuery(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(h);
  }, [searchInput]);

  // Super-admin: load tenants once for the scope selector.
  useEffect(() => {
    if (!isSuperAdmin) return;
    tenantAPI.getAll().then((res) => {
      if (res.data.success) setTenants(res.data.data.tenants);
    }).catch(() => { /* non-fatal */ });
  }, [isSuperAdmin]);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, channel, query, from, to, page, limit, tenantId, contactId]);

  const loadList = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await messageAPI.getOverview({
        page, limit, status, channel,
        search: query || undefined,
        from: from || undefined,
        to: to || undefined,
        tenant_id: isSuperAdmin && tenantId ? Number(tenantId) : undefined,
        contactId: contactId || undefined,
      });
      if (res.data.success) {
        const d = res.data.data;
        setMessages(d.messages);
        setSummary(d.summary);
        setTotalPages(d.pagination.totalPages);
        setTotal(d.pagination.total);
        setForbidden(false);
      }
    } catch (err: any) {
      if (err.response?.status === 403) { setForbidden(true); setMessages([]); setSummary(null); }
      else setError(err.response?.data?.error || t('Učitavanje nije uspelo', 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await messageAPI.getDetail(id, isSuperAdmin && tenantId ? Number(tenantId) : undefined);
      if (res.data.success) setDetail(res.data.data);
    } catch {
      /* keep modal open with a not-found message */
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetailId(null); setDetail(null); };

  const resetFilters = () => {
    setStatus('all'); setChannel('all'); setSearchInput(''); setQuery('');
    setFrom(''); setTo(''); setPage(1);
  };

  const hasFilters = status !== 'all' || channel !== 'all' || !!query || !!from || !!to;

  const selectedTenantName = useMemo(
    () => tenants.find((x) => x.id === Number(tenantId))?.name,
    [tenants, tenantId]
  );

  // Label for the per-contact banner, derived from the first row's recipient.
  const contactLabel = useMemo(() => {
    if (!contactId) return '';
    const r = messages[0]?.recipient;
    return r ? (r.name || r.email || r.phone || `#${contactId}`) : `#${contactId}`;
  }, [contactId, messages]);

  const clearContact = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('contactId');
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  if (forbidden) {
    return (
      <div className="page page-wide">
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('Pregled poruka', 'Messages')}</h1>
          </div>
        </div>
        <div className="card card-pad" style={{ borderColor: 'var(--warn)', color: 'var(--text)' }}>
          <Icon name="shield" size={16} />{' '}
          {t('Pregled poslatih poruka nije omogućen za vaš nalog. Obratite se administratoru.',
            'The sent-messages overview is not enabled for your account. Please contact your administrator.')}
        </div>
      </div>
    );
  }

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Pregled poruka', 'Messages')}</h1>
          <p className="page-sub">
            {t('Sve poslate poruke', 'All sent messages')}
            {isSuperAdmin && selectedTenantName ? ` — ${selectedTenantName}` : ''}
            {total ? ` · ${fmt(total)}` : ''}
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-8">
            <span className="dim" style={{ fontSize: 13 }}><Icon name="building" size={15} /> {t('Klijent', 'Client')}</span>
            <select
              className="input input-sm"
              value={tenantId}
              onChange={(e) => { setTenantId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
              aria-label={t('Izaberi klijenta', 'Select client')}
            >
              <option value="">{t('Svi klijenti', 'All clients')}</option>
              {tenants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 'var(--gap)' }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}

      {/* per-contact scope banner */}
      {contactId > 0 && (
        <div className="card card-pad flex items-center justify-between wrap gap-12"
          style={{ marginBottom: 'var(--gap)', borderColor: 'var(--brand-500)', background: 'var(--brand-50)' }}>
          <span className="flex items-center gap-8" style={{ fontSize: 13.5 }}>
            <Icon name="contacts" size={16} />
            {t('Prikazane su poruke za kontakt:', 'Showing messages for contact:')} <strong>{contactLabel}</strong>
          </span>
          <button className="btn btn-ghost btn-sm" onClick={clearContact}>
            <Icon name="x" size={14} /> {t('Prikaži sve poruke', 'Show all messages')}
          </button>
        </div>
      )}

      {/* summary tiles */}
      <div className="stat-grid" style={{ marginBottom: 'var(--gap)' }}>
        <StatTile icon="send" channel="sms" label={t('Ukupno poslato', 'Total sent')} value={fmt(summary?.total ?? 0)} />
        <StatTile icon="checkCircle" channel="email" label={t('Isporučeno', 'Delivered')} value={fmt(summary?.delivered ?? 0)} />
        <StatTile icon="clock" label={t('Neisporučeno', 'Undelivered')} value={fmt(summary?.undelivered ?? 0)} />
        <StatTile icon="alert" channel="viber" label={t('Neuspelo', 'Failed')} value={fmt(summary?.failed ?? 0)} />
      </div>

      {/* filters + table */}
      <div className="card">
        <div className="card-pad flex items-center justify-between wrap gap-12" style={{ paddingBottom: 12 }}>
          <div className="flex items-center gap-8 wrap">
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>
                <Icon name="search" size={15} />
              </span>
              <input
                className="input input-sm"
                style={{ paddingLeft: 30, minWidth: 220 }}
                placeholder={t('Pretraga: ime, email, telefon, kampanja', 'Search: name, email, phone, campaign')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select className="input input-sm" value={channel} onChange={(e) => { setChannel(e.target.value); setPage(1); }} aria-label={t('Kanal', 'Channel')}>
              <option value="all">{t('Svi kanali', 'All channels')}</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="viber">Viber</option>
            </select>
            <select className="input input-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('Status', 'Status')}>
              <option value="all">{t('Svi statusi', 'All statuses')}</option>
              <option value="delivered">{t('Isporučeno', 'Delivered')}</option>
              <option value="sent">{t('Poslato', 'Sent')}</option>
              <option value="undelivered">{t('Neisporučeno', 'Undelivered')}</option>
              <option value="failed">{t('Neuspelo', 'Failed')}</option>
              <option value="pending">{t('Na čekanju', 'Pending')}</option>
            </select>
            <input className="input input-sm" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label={t('Od', 'From')} title={t('Od', 'From')} />
            <input className="input input-sm" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label={t('Do', 'To')} title={t('Do', 'To')} />
            {hasFilters && (
              <button className="btn btn-ghost btn-sm" onClick={resetFilters}>
                <Icon name="x" size={14} /> {t('Poništi', 'Clear')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-8">
            <select className="input input-sm" value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }} aria-label={t('Po strani', 'Per page')}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="dim" style={{ fontSize: 13 }}>{fmt(total)}</span>
          </div>
        </div>

        {loading && messages.length === 0 ? (
          <div className="card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
        ) : messages.length === 0 ? (
          <Empty
            icon="inbox"
            title={hasFilters ? t('Nema rezultata', 'No results') : t('Još nema poslatih poruka', 'No sent messages yet')}
            sub={hasFilters ? t('Promenite filtere i pokušajte ponovo.', 'Adjust the filters and try again.') : t('Kada počnete da šaljete, poruke će se pojaviti ovde.', 'Once you start sending, messages will appear here.')}
          />
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 22 }}>{t('Primalac', 'Recipient')}</th>
                  <th>{t('Kampanja', 'Campaign')}</th>
                  <th>{t('Kanal', 'Channel')}</th>
                  <th>{t('Datum', 'Date')}</th>
                  <th>{t('Status', 'Status')}</th>
                  {isSuperAdmin && <th>{t('Trošak', 'Cost')}</th>}
                  <th style={{ textAlign: 'right', paddingRight: 22 }}>{t('Akcija', 'Action')}</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => {
                  const contact = m.recipient?.email || m.recipient?.phone || '—';
                  return (
                    <tr key={m.id}>
                      <td style={{ paddingLeft: 22 }}>
                        <div className="flex items-center gap-12">
                          <span className="av-mini" style={{ background: 'var(--brand-600)' }}>{initials(m.recipient?.name, contact)}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{m.recipient?.name || '—'}</div>
                            <div className="muted mono" style={{ fontSize: 12.5 }}>{contact}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-8">
                          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{m.campaign?.name || '—'}</span>
                          {m.campaign?.isTransactional && (
                            <span className="chip" title={t('Pojedinačna poruka', 'Transactional message')} style={{ fontSize: 11 }}>
                              {t('Pojedinačna', 'Single')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td><ChannelBubble channel={m.channel as Channel} size="sm" /></td>
                      <td className="muted" style={{ fontSize: 13 }}>{fmtDate(m.sentAt || undefined)}</td>
                      <td>{statusBadge(m.status)}</td>
                      {isSuperAdmin && (
                        <td className="tnum muted" style={{ fontSize: 13 }}>{m.cost != null ? `$${m.cost.toFixed(4)}` : '—'}</td>
                      )}
                      <td style={{ textAlign: 'right', paddingRight: 22 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openDetail(m.id)}>
                          <Icon name="eye" size={15} /> {t('Pregled', 'View')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="card-pad" style={{ paddingTop: 12 }}>
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* detail modal */}
      {detailId != null && (
        <div className="overlay" onClick={closeDetail}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="flex items-center gap-12">
                {detail && <ChannelBubble channel={detail.channel as Channel} size="sm" />}
                <div>
                  <h3 className="card-title">{t('Poruka', 'Message')}{detail?.campaign ? ` — ${detail.campaign.name}` : ''}</h3>
                  {detail?.recipient && (
                    <div className="dim" style={{ fontSize: 12.5 }}>
                      {detail.recipient.name ? `${detail.recipient.name} · ` : ''}
                      {detail.recipient.email || detail.recipient.phone || ''}
                    </div>
                  )}
                </div>
              </div>
              <button className="icon-btn" aria-label={t('Zatvori', 'Close')} onClick={closeDetail}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              {detailLoading ? (
                <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
              ) : !detail ? (
                <div className="card-pad" style={{ color: 'var(--danger-strong)' }}>
                  <Icon name="alert" size={16} /> {t('Poruka nije pronađena.', 'Message not found.')}
                </div>
              ) : (
                <>
                  {/* meta grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
                    {([
                      [t('Status', 'Status'), statusBadge(detail.status)],
                      [t('Poslato', 'Sent'), fmtDateTime(detail.sentAt)],
                      [t('Isporučeno', 'Delivered'), fmtDateTime(detail.deliveredAt)],
                      detail.seenAt ? [t('Viđeno', 'Seen'), fmtDateTime(detail.seenAt)] : null,
                      detail.error ? [t('Greška', 'Error'), <span style={{ color: 'var(--danger-strong)' }}>{detail.error}</span>] : null,
                      isSuperAdmin ? [t('Trošak', 'Cost'), detail.cost != null ? `$${detail.cost.toFixed(4)}` : '—'] : null,
                      detail.messageId ? [t('ID poruke', 'Message ID'), <span className="mono" style={{ fontSize: 12 }}>{detail.messageId}</span>] : null,
                    ].filter(Boolean) as [string, React.ReactNode][]).map(([label, val], i) => (
                      <div key={i}>
                        <div className="dim" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 13.5, fontWeight: 600 }}>{val}</div>
                      </div>
                    ))}
                  </div>

                  {/* content */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    {detail.content.channel === 'email' ? (
                      <>
                        {detail.content.subject && (
                          <div style={{ marginBottom: 12 }}>
                            <span className="dim" style={{ fontSize: 12 }}>{t('Naslov', 'Subject')}: </span>
                            <strong>{detail.content.subject}</strong>
                          </div>
                        )}
                        <iframe
                          title="email-preview"
                          sandbox=""
                          srcDoc={detail.content.html || `<p style="color:#888;font-family:sans-serif">${t('Sadržaj nije dostupan.', 'Content unavailable.')}</p>`}
                          style={{ width: '100%', height: 460, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: '#fff' }}
                        />
                      </>
                    ) : detail.content.channel === 'viber' && detail.content.viber ? (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <ViberMessagePreview
                          viberContent={{
                            text: detail.content.viber.text,
                            label: 'PROMOTION',
                            image: detail.content.viber.image || undefined,
                            button: detail.content.viber.button || undefined,
                            ttl: 86400,
                          }}
                          recipientData={{
                            name: detail.recipient?.name || '',
                            phoneNumber: detail.recipient?.phone || '',
                            customData: {},
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ maxWidth: 480 }}>
                        <div className="dim" style={{ fontSize: 12, marginBottom: 8 }}>{t('Tekst poruke', 'Message text')}</div>
                        <div style={{
                          background: 'var(--ch-sms-soft)', color: 'var(--text)', padding: '14px 16px',
                          borderRadius: 16, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {detail.content.text || t('Sadržaj nije dostupan.', 'Content unavailable.')}
                        </div>
                        {detail.content.fallbackSms && (
                          <div className="hint" style={{ marginTop: 10 }}>
                            <strong>{t('SMS rezerva', 'SMS fallback')}:</strong> {detail.content.fallbackSms}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOverview;
